// activity_manager.jsx
// Every activation, refused activation and deactivation across all licenses,
// newest first: the "which license went onto which computer, from where, and
// when" log. A row opens its license.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Activity, Search, RefreshCw, CheckCircle2, Ban, Unplug, RotateCcw } from 'lucide-react';
import { useAutoLey } from '../../authentication_components/autoleyContext';
import {
  Section, Button, Loading, EmptyState, Pagination, Badge, inputClass, controlClass, fmtDateTime, osLabel, accountName, actorLabel,
} from './autoley_ui';

const PAGE_SIZE = 50;

const KINDS = [
  { id: '', label: 'Everything' },
  { id: 'activated', label: 'Activations' },
  { id: 'reactivated', label: 'Reactivations (same computer)' },
  { id: 'refused', label: 'Refused (device limit)' },
  { id: 'deactivated', label: 'Deactivations' },
];

const WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
];

function KindBadge({ kind }) {
  switch (kind) {
    case 'activated':
      return <Badge icon={CheckCircle2} className="bg-emerald-50 text-emerald-700 ring-emerald-200">Activated</Badge>;
    case 'reactivated':
      return <Badge icon={RotateCcw} className="bg-emerald-50 text-emerald-700 ring-emerald-200">Reactivated</Badge>;
    case 'refused':
      return <Badge icon={Ban} className="bg-rose-50 text-rose-700 ring-rose-200">Refused</Badge>;
    default:
      return <Badge icon={Unplug} className="bg-slate-100 text-slate-600 ring-slate-200">Deactivated</Badge>;
  }
}

export default function ActivityManager({ onOpenLicense }) {
  const { fetchActivity } = useAutoLey();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [days, setDays] = useState(30);
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState({ events: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); setSearch(searchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await fetchActivity({ kind: kind || undefined, search: search || undefined, days, limit: PAGE_SIZE, offset });
      setData({ events: result?.events || [], total: result?.total || 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to load activation activity');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchActivity, kind, search, days, offset]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load({ silent: true }); };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-6 sm:px-4 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-800">Activation activity</h2>
          <p className="text-sm text-slate-500">Which license was activated on which computer, from which IP, and when.</p>
        </div>
        <Button variant="secondary" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Section title="Activity" icon={Activity} className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Email, name, computer name, IP or key…"
              className={`${inputClass} pl-9`}
            />
          </div>
          <select value={kind} onChange={(e) => { setOffset(0); setKind(e.target.value); }} className={`${controlClass} w-52`}>
            {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          <select value={days} onChange={(e) => { setOffset(0); setDays(Number(e.target.value)); }} className={`${controlClass} w-40`}>
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </div>

        {loading && data.events.length === 0 ? (
          <Loading label="Loading activity…" />
        ) : data.events.length === 0 ? (
          <div className="p-4"><EmptyState icon={Activity} title="No activity in this window" hint="Widen the time window or clear the search." /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">What</th>
                    <th className="px-4 py-3">Computer</th>
                    <th className="px-4 py-3">IP</th>
                    <th className="px-4 py-3">License</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.events.map((e) => (
                    <tr key={e.id} className="cursor-pointer hover:bg-blue-50/40" onClick={() => onOpenLicense?.(e.license_id)}>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDateTime(e.at)}</td>
                      <td className="px-4 py-3">
                        <KindBadge kind={e.kind} />
                        {e.kind === 'deactivated' && <div className="mt-0.5 text-xs text-slate-400">by {actorLabel(e.actor)}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{e.device_name || '—'}</div>
                        <div className="text-xs text-slate-400">{osLabel(e.os)}{e.app_version ? ` · v${e.app_version}` : ''}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-600">{e.ip || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-700">{accountName(e.account)}</div>
                        <div className="text-xs text-slate-400">{e.account?.email} · key …{e.key_hint}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination total={data.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} noun="event" />
          </>
        )}
      </Section>
    </div>
  );
}
